import { Component, h } from 'preact'
import Panel from '../panel'
import IntegrationHeader from './IntegrationHeader'
import ButtonControl from '../button-control'
import axios from 'axios'
import InputGroup from '../ui-components/InputGroup'
import AlertComponent from '../ui-components/AlertComponent'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import globals from '../../utils/globals'
import Switcher from '../control/Switcher'
import Header from '../header'
import Tooltip from '../ui-components/Tooltip'

interface Props {
 csrfToken: string,
 isIntegrationsHomepage: boolean
}

interface State {
  isLoading?: boolean,
  isInstalled: boolean,
  messageType?: string|null,
  message?: string,
  subdomain: string,
  apiToken: string,
  enabledFederatedSearch: boolean,
  syncInProgress: boolean,
  showConfirmationMessage: boolean
}

class SkilljarIntegration extends Component<Props, State> {
  headerDescription: string

  constructor (props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      isInstalled: false,
      messageType: null,
      message: '',
      apiToken: '',
      subdomain: '',
      enabledFederatedSearch: false,
      syncInProgress: false,
      showConfirmationMessage: false
    }
    this.headerDescription = 'Search for Skilljar courses through your inSided platform with federated search'
    this.installSkilljar = this.installSkilljar.bind(this)
    this.toggleFederatedSearch = this.toggleFederatedSearch.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.onSync = this.onSync.bind(this)
  }

  async installSkilljar () {
    const successMessage = 'Skilljar integration is installed successfully!'
    let errorMessage: string = 'Unable to save configuration for skilljar integration.'
    this.setState({ messageType: null, isLoading: true })
    try {
      
      await axios.post('/integration/skilljar/settings', {
        apiToken: this.state.apiToken,
        subdomain: this.state.subdomain
      })

      this.setState({ isInstalled: true, isLoading: false, messageType: 'success', message: successMessage })
    } catch {
      this.setState({ isLoading: false, messageType: 'error', message: errorMessage })
    }
  }

  async toggleFederatedSearch () {
    try {
      this.setState({ messageType: null })
      const response = await axios.post('/integration/skilljar/toggleFederatedSearch', {
        isActive: !this.state.enabledFederatedSearch
      })
      if (response.data) {
        const successMessage = this.state.enabledFederatedSearch ? 'Federated search is disabled on your community' : `Federated search is enabled on your community. ${response.data.count} Skilljar courses are successfully synchronized!`

        this.setState({
          enabledFederatedSearch: !this.state.enabledFederatedSearch,
          messageType: 'success',
          message: successMessage
        })
      }
    } catch {
      this.setState({
        messageType: 'error',
        message: 'Sorry, synchronisation failed, please try again.'
      })
    }
  }

  async onSync () {
    this.setState({ syncInProgress: true, messageType: null })
    try {
      const res = await axios.post('/integration/skilljar/sync')
      const successMessage = `${res.data.count} Skilljar courses are successfully synchronized!`
      this.setState({ syncInProgress: false,
        messageType: 'success',
        message: successMessage })
    } catch {
      this.setState({
        syncInProgress: false,
        messageType: 'error',
        message: 'Sorry, synchronisation failed, please try again.'
      })
    }
  }

  async onDelete () {
    this.setState({ isLoading: true, messageType: null })
    const errorMessage = 'Unable to delete credentials for skilljar integration.'
    try {
      await axios.delete('/integration/skilljar/settings', { data: { csrfToken: this.props.csrfToken } })
      this.setState({
        apiToken: '',
        subdomain: '',
        isLoading: false,
        enabledFederatedSearch: false,
        isInstalled: false,
        showConfirmationMessage: false
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
  }

  async componentDidMount () {
    this.setState({ messageType: null })
    try {
      const res = await axios.get('/integration/skilljar/settings')
      if (res.data.apiToken) {
        this.setState({ apiToken: res.data.apiToken, subdomain: res.data.subdomain, isInstalled: true, enabledFederatedSearch: res.data.enabledFederatedSearch })
      }
    } catch {
      this.setState({ isInstalled: false })
    }
  }

  render (props: Props, { apiToken, subdomain, isLoading, isInstalled, message, messageType, enabledFederatedSearch, showConfirmationMessage, syncInProgress }: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const getDeleteConfirmationButtons = () => {
      return [
        {
          text: 'Confirm',
          class: 'btn btn-danger qa-confirm',
          showLoading: true,
          callback: () => this.onDelete()
        },
        {
          text: 'Cancel',
          class: 'btn btn-default',
          showLoading: false,
          callback: () => this.setState({ showConfirmationMessage: false })
        }
      ]
    }

    if (props.isIntegrationsHomepage) {
      return (
        <div class='integrations-skilljar'>
          <Panel
            title={<IntegrationHeader
              isInstalled={isInstalled}
              name='Skilljar'
              description={this.headerDescription}>
              <img style={{ width: '56px' }} src={`${globals('basePath')}images/skilljar.png`} />
            </IntegrationHeader>} />
        </div>
      )
    }

    return (
      <div class='integrations-skilljar'>
        <Header
          title='Skilljar'
          disableCancelButton
          disableSaveButton
          hasBreadcrumb
          breadcrumbText='Back to integrations overview'
          breadcrumbLink='/settings/integrations'
          statusLabelText={isInstalled ? 'Installed' : ''}
          statusLabelSuccess
        />
        <Panel title='Integration setup' collapsible={isInstalled}>
          <InputGroup
            title='API token *'
            type='text'
            qaClasses='qa-skilljar-api-key-input'
            value={apiToken}
            readOnly={isInstalled}
            onChange={(e) => this.setState({ apiToken: e.target.value })}
          />
          <InputGroup
            title='Subdomain *'
            type='text'
            qaClasses='qa-skilljar-subdomain-input'
            value={subdomain}
            readOnly={isInstalled}
            onChange={(e) => this.setState({ subdomain: e.target.value })}
          />
          {
            isInstalled ? (
              <ButtonControl
                variant='warning'
                title='Uninstall and delete credentials'
                class={'qa-install'}
                onClick={() => { this.setState({ showConfirmationMessage: true }) }}
              />
            ) : (
              <ButtonControl
                variant='primary'
                title='Install'
                class={'qa-install'}
                onClick={this.installSkilljar}
              />
            )
          }
          {
            messageType ? <AlertComponent messageType={messageType} message={message} sticky isVisibleFor={isVisibleFor} /> : null
          }
          <ConfirmationModal
            isLoading={isLoading}
            enableModal={showConfirmationMessage}
            title='Uninstall and delete credentials?'
            message='Your users will no longer be able to see and search for Skilljar courses'
            buttons={getDeleteConfirmationButtons()}
            onClose={() => this.setState({ showConfirmationMessage: false })}
          />
        </Panel>
        {
          isInstalled ? (
            <Panel title='Federated search' collapsible customClass='skilljar-federated-search-panel' >
              <div className='skilljar-federated-search-panel-wrapper'>
                <Switcher
                  on={enabledFederatedSearch}
                  label='Enable federated search'
                  onChange={this.toggleFederatedSearch} />
                {
                  enabledFederatedSearch ? (
                    <div>
                      <ButtonControl
                        variant='secondary'
                        title='Synchronize courses'
                        isLoading={syncInProgress}
                        onClick={this.onSync} />
                      <Tooltip
                        trigger={<i className='fa fa-question-circle' />}
                        customClasses='integrations-zendesk__sync-tooltip'
                        tooltipMessage='Search index from Skilljar are updated every day. Click this button to trigger a full synchronisation if needed.'
                      />
                    </div>
                  ) : null
                }
              </div>
            </Panel>
          ) : null
        }
      </div>
    )
  }
}

export default SkilljarIntegration
